import * as React from "react";

import testChargeData from "app/constants/testChargeData";
import Charge, {
  ChargeAsset,
  ChargeMode,
  ChargePricing,
  ChargeTimeline,
  Merchant,
  PaymentCharge,
  UserPayload,
} from "app/models/charge";

import { DispatchAction, PaymentMethodType, SourceType } from "./types";
import { CreatePaymentResponse } from "app/api/payment/types";
import { PAYMENT_REQUEST } from "app/constants/variables";

export type AppStateContextType = {
  paymentMethod?: PaymentMethodType;
  bushaAuthToken?: string;
  merchant: Merchant;
  activeAsset?: ChargeAsset;
  paymentComplete?: boolean;
  mode: ChargeMode;
  displayMode: "INLINE" | "REDIRECT";
  parentOrigin?: string;
  payment?: CreatePaymentResponse;
  source?: SourceType;
  sourceId?: string;
  userPayload?: UserPayload;
  devModeState?: DevModeFormState;
};

export type DevModeFormState = {
  public_key: string;
  local_amount: string;
  local_currency: string;
  email: string;
  name: string;
  target_currency: string;
  source_currency: string;
  quote_currency: string;
  quote_amount: string;
};

type AppActionType = DispatchAction<
  | Partial<AppStateContextType>
  | PaymentCharge
  | ChargeAsset
  | ChargeTimeline
  | string
  | ChargePricing[]
  | CreatePaymentResponse
  | UserPayload
  | DevModeFormState
>;

const bushaPayCharge = (window as any).bushaPayCharge ?? {};

const merchant: Merchant = bushaPayCharge.merchant ?? testChargeData;

const INITIAL_STATE: AppStateContextType = {
  merchant,
  mode: bushaPayCharge.mode ?? "live",
  displayMode: bushaPayCharge.displayMode ?? "REDIRECT",
  parentOrigin: bushaPayCharge.parentOrigin,
  source: bushaPayCharge.source ?? PAYMENT_REQUEST,
  sourceId: bushaPayCharge.sourceId ?? undefined,
  userPayload: {} as UserPayload,
  devModeState: {
    public_key: "pub_Ar5i7rySZuMRregSaCxb9",
    local_amount: "500",
    local_currency: "NGN",
    email: "bolaji@busha.co",
    name: "Abolaji Bisiriyu",
    target_currency: "",
    source_currency: "",
    quote_currency: "",
    quote_amount: "",
  },
};

export const AppStateActions = {
  setAppState: "SET_APP_STATE", // for dev use only ⚠️
  setCharge: "SET_CHARGE",
  updateChargeTimeline: "UPDATE_CHARGE_TIMELINE",
  completePayment: "COMPLETE_PAYMENT",
  setActiveAsset: "SET_ACTIVE_ASSET",
  setBushaAuthToken: "SET_BUSHA_AUTH_TOKEN",
  setPayment: "SET_PAYMENT",
  setUserPayload: "SET_USER_PAYLOAD",
  setMerchant: "SET_MERCHANT",
  setDevModeState: "SET_DEVMODE_STATE",
};

function appStateReducer(
  state: AppStateContextType,
  action: AppActionType
): AppStateContextType {
  switch (action.type) {
    case AppStateActions.setAppState:
      return { ...state, ...(action.payload as Partial<AppStateContextType>) };

    case AppStateActions.setCharge:
      return { ...state, merchant: action.payload as Charge };

    case AppStateActions.updateChargeTimeline:
      return {
        ...state,
        merchant: {
          ...state.merchant,
        },
      };
    case AppStateActions.setMerchant:
      return {
        ...state,
        merchant: {
          ...state.merchant,
          ...(action.payload as Partial<Merchant>),
        },
      };

    case AppStateActions.setActiveAsset:
      return { ...state, activeAsset: action.payload as ChargeAsset };

    case AppStateActions.completePayment:
      return { ...state, paymentComplete: true };

    case AppStateActions.setBushaAuthToken:
      return { ...state, bushaAuthToken: action.payload as string };

    case AppStateActions.setPayment:
      return { ...state, payment: action.payload as CreatePaymentResponse };

    case AppStateActions.setUserPayload:
      return {
        ...state,
        userPayload: action.payload as UserPayload,
      };

    case AppStateActions.setDevModeState:
      return {
        ...state,
        devModeState: action.payload as DevModeFormState,
      };

    default:
      throw new Error();
  }
}

export const AppStateContext =
  React.createContext<AppStateContextType>(INITIAL_STATE);
AppStateContext.displayName = "AppStateContext";

export const AppStateDispatchContext = React.createContext<
  React.Dispatch<AppActionType>
>(() => {});
AppStateDispatchContext.displayName = "AppStateDispatchContext";

export default function AppStateProvider(props: React.PropsWithChildren<any>) {
  const [tokens, dispatch] = React.useReducer(appStateReducer, INITIAL_STATE);

  return (
    <AppStateContext.Provider value={tokens}>
      <AppStateDispatchContext.Provider value={dispatch}>
        {props.children}
      </AppStateDispatchContext.Provider>
    </AppStateContext.Provider>
  );
}

export function useAppState() {
  const appState = React.useContext(AppStateContext);

  if (typeof appState === "undefined") {
    throw new Error("useAppState must be used within a AppStateProvider");
  }

  return appState;
}

export function useAppDispatch() {
  const appDispatch = React.useContext(AppStateDispatchContext);

  if (typeof appDispatch === "undefined") {
    throw new Error("useAppDispatch must be used within a AppStateProvider");
  }

  return appDispatch;
}

export function useApp(): [AppStateContextType, React.Dispatch<AppActionType>] {
  return [useAppState(), useAppDispatch()];
}
